import { useProjectStore } from "../store/useProjectStore";
import { generateFoundationWithGroq, generateHypothesisInsightsWithGroq } from "./generateWithGroq.js";
import { makeEmptyProject } from "./normalizeProject";
import {
  countExtractedCharacters,
  getExtractedTextLimitError,
  normalizeAIProcessingError,
} from "./aiProcessingGuards";
import { getPlanLimitsForUser } from "./planLimits";
import {
  applyDemoGenerationToProject,
  getDemoBrowserId,
  getDemoProjectState,
  isSharedDemoUser,
} from "./demoFlow";

function hash32(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return (h >>> 0).toString(16);
}

function computeInsightsHash({ projectId = "", initiativeId = "", insights = [] } = {}) {
  const normalized = (Array.isArray(insights) ? insights : [])
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .join("\n");
  return hash32(`${projectId}::${initiativeId}::${normalized}`);
}

function normalizeDocText(s) {
  return String(s || "")
    .replace(/\r/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function attachEvidenceOffsetsToContainer(container, researchItems) {
  if (!container || typeof container !== "object") return container;
  if (!Array.isArray(container.items)) return container;
  const items = container.items.map((ins) => {
    const evidence = Array.isArray(ins.evidence) ? ins.evidence : [];
    if (!evidence.length) return ins;

    const matched = evidence.map((ev) => {
      const researchItemId = String(ev.researchItemId || "").trim();
      const doc = researchItems.find((ri) => String(ri.id) === researchItemId);
      if (!doc) return ev;
      const raw = String(doc.rawText || "");
      const text = String(ev.text || "").trim();
      if (!text) return ev;
      const start = raw.indexOf(text);
      if (start === -1) return ev;
      return { ...ev, start, end: start + text.length };
    });

    return { ...ins, evidence: matched };
  });

  return { ...container, items };
}

function attachEvidenceOffsetsToAiCore(aiCore, researchItems) {
  if (!aiCore || typeof aiCore !== "object") return aiCore;
  const next = { ...aiCore };
  if (next.researchInsights && typeof next.researchInsights === "object") {
    next.researchInsights = attachEvidenceOffsetsToContainer(next.researchInsights, researchItems);
  }
  return next;
}

function ensureHypothesisInsightsOnAiCore(aiCoreRaw, hypothesisInsights) {
  const base = aiCoreRaw && typeof aiCoreRaw === "object" ? aiCoreRaw : {};
  const insightsContainer =
    hypothesisInsights && typeof hypothesisInsights === "object"
      ? hypothesisInsights
      : { version: 4, items: [], createdAt: Date.now(), updatedAt: Date.now(), emergingSignals: { items: [] } };

  return {
    ...base,
    researchInsights: insightsContainer,
    overview: {
      ...(base.overview && typeof base.overview === "object" ? base.overview : {}),
      researchInsights: Array.isArray(insightsContainer.items)
        ? insightsContainer.items.map((item) => ({
            title: item?.title || "",
            text: item?.text || "",
            category: item?.category || "Other",
            source: "hypothesis",
            sourceType: "ai_suggested",
            confidence: "hypothesis",
            tags: Array.isArray(item?.tags) ? item.tags : [],
            factIds: [],
          }))
        : [],
    },
    emergingSignals:
      insightsContainer?.emergingSignals && typeof insightsContainer.emergingSignals === "object"
        ? insightsContainer.emergingSignals
        : { items: [], generatedAt: new Date().toISOString() },
    tagVocabulary: base?.tagVocabulary && typeof base.tagVocabulary === "object" ? base.tagVocabulary : { version: 1, items: [] },
    tagThemes: base?.tagThemes && typeof base.tagThemes === "object" ? base.tagThemes : { version: 1, themes: [] },
    researchTagSpans:
      base?.researchTagSpans && typeof base.researchTagSpans === "object"
        ? base.researchTagSpans
        : { version: 1, items: [] },
  };
}

export async function runProjectGeneration({
  storage,
  projectId,
  formData,
  stagedItemsList,
}) {
  if (!projectId) throw new Error("Missing project id.");

  const store = useProjectStore.getState();
  const {
    setActiveProject,
    setMeta,
    setFromAI,
    setResearchItems,
    setResearchLastAIGenerationAt,
    syncActiveProjectSnapshot,
  } = store;

  // Ensure all store writes in this function target the project being generated.
  if (typeof setActiveProject === "function") {
    setActiveProject(projectId);
  }

  const isDemoUser = isSharedDemoUser(store?.currentUser);
  const demoBrowserId = getDemoBrowserId();
  const demoState = isDemoUser ? getDemoProjectState(store?.selectActiveRefs?.()?.project, demoBrowserId) : null;
  if (isDemoUser && demoState?.generationBlocked) {
    throw new Error("Another visitor is currently generating content in this demo project.");
  }

  const metaPayload = {
    projectName: formData.projectName.trim(),
    productDescription: formData.productDescription,
    targetAudience: formData.targetAudience,
    industry: formData.industry,
    projectType: formData.projectType,
    projectMode: formData.projectType === "existing" ? "existing" : "new",
    slug: "",
    wizardCompleted: true,
  };

  const tNow = Date.now();
  let demoGenerationMarked = false;
  const researchItems = (Array.isArray(stagedItemsList) ? stagedItemsList : []).map((item) => ({
    ...item,
    rawText: normalizeDocText(item?.rawText || item?.text || ""),
    normalizedText: normalizeDocText(item?.rawText || item?.text || ""),
    updatedAt: tNow,
    personaId: null,
  }));
  const hasNoResearchItems = researchItems.length === 0;
  const extractedCharCount = countExtractedCharacters(researchItems);
  const currentPlanLimits = getPlanLimitsForUser(store?.currentUser || { plan: isDemoUser ? "demo" : "free" });
  const extractedTextLimitError =
    store?.currentUser?.role === "admin"
      ? null
      : getExtractedTextLimitError(extractedCharCount, currentPlanLimits.researchExtractedTextMax);
  let usedHypothesisFallback = false;

  if (extractedTextLimitError) {
    if (typeof store?.setGenerationError === "function") {
      store.setGenerationError(extractedTextLimitError);
    }
    if (typeof store?.setIsGeneratingProject === "function") {
      store.setIsGeneratingProject(true);
    }
    throw extractedTextLimitError;
  }

  let aiCoreRaw;
  if (hasNoResearchItems) {
    try {
      const hypothesisInsights = await generateHypothesisInsightsWithGroq({ ...formData, projectId }, {
        minItems: 5,
        maxItems: 10,
      });
      aiCoreRaw = ensureHypothesisInsightsOnAiCore({}, hypothesisInsights);
      usedHypothesisFallback = true;
    } catch (err) {
      const nextError = normalizeAIProcessingError(err);
      if (typeof store?.setGenerationError === "function") {
        store.setGenerationError(nextError);
      }
      if (typeof store?.setIsGeneratingProject === "function") {
        store.setIsGeneratingProject(true);
      }
      if (demoGenerationMarked && typeof setMeta === "function") {
        setMeta({
          demoGenerationInProgress: false,
          demoGenerationStartedAt: null,
          demoGenerationHeartbeat: null,
          demoGenerationBrowserId: demoBrowserId,
        });
      }
      throw nextError;
    }
  } else {
    try {
      aiCoreRaw = await generateFoundationWithGroq({
        ...formData,
        projectId,
        researchItems,
        researchInterviewNotes: "",
        researchCustomerMessages: "",
        researchSupportTickets: "",
        researchTranscripts: "",
      });
    } catch (err) {
      const nextError = normalizeAIProcessingError(err);
      if (typeof store?.setGenerationError === "function") {
        store.setGenerationError(nextError);
      }
      if (typeof store?.setIsGeneratingProject === "function") {
        store.setIsGeneratingProject(true);
      }
      if (demoGenerationMarked && typeof setMeta === "function") {
        setMeta({
          demoGenerationInProgress: false,
          demoGenerationStartedAt: null,
          demoGenerationHeartbeat: null,
          demoGenerationBrowserId: demoBrowserId,
        });
      }
      throw nextError;
    }
  }

  const generatedInsightsCount = Array.isArray(aiCoreRaw?.researchInsights?.items)
    ? aiCoreRaw.researchInsights.items.filter((it) => String(it?.text || "").trim()).length
    : 0;

  if (hasNoResearchItems && generatedInsightsCount === 0) {
    const hypothesisInsights = await generateHypothesisInsightsWithGroq({ ...formData, projectId }, {
      minItems: 5,
      maxItems: 10,
    });
    aiCoreRaw = ensureHypothesisInsightsOnAiCore(aiCoreRaw, hypothesisInsights);
    usedHypothesisFallback = true;
  }

  if (Array.isArray(aiCoreRaw?.promptFacts) && aiCoreRaw.promptFacts.length) {
    metaPayload.promptFacts = aiCoreRaw.promptFacts;
    metaPayload.promptFactsUpdatedAt = Date.now();
  }

  const researchItemsWithPersona = researchItems.map((item) => ({
    ...item,
    personaId: null,
  }));

  const aiCore = attachEvidenceOffsetsToAiCore(aiCoreRaw, researchItemsWithPersona);
  if (aiCore && typeof aiCore === "object") {
    if (
      aiCore.emergingSignals &&
      aiCore.researchInsights &&
      typeof aiCore.researchInsights === "object" &&
      !aiCore.researchInsights.emergingSignals
    ) {
      aiCore.researchInsights = {
        ...aiCore.researchInsights,
        emergingSignals: aiCore.emergingSignals,
      };
    }
  }

  const canonicalItems =
    aiCore?.researchInsights && typeof aiCore.researchInsights === "object"
      ? aiCore.researchInsights.items
      : [];

  const insightStrings = (Array.isArray(canonicalItems) ? canonicalItems : [])
    .map((x) => String(x?.text || "").trim())
    .filter(Boolean);

  const insightsHash = computeInsightsHash({
    projectId: String(projectId || ""),
    initiativeId: "active",
    insights: insightStrings,
  });

  const directionDraft =
    aiCore?.direction && typeof aiCore.direction === "object" ? aiCore.direction.draft : null;

  const directionPayload = {
    draft: directionDraft
      ? {
          ...directionDraft,
          generatedAt: Date.now(),
          source: {
            kind: "wizard_unreviewed_ai",
            insightsHash,
            insightCount: insightStrings.length,
          },
        }
      : null,
    final: null,
    updatedAt: Date.now(),
  };

  const audienceGroupsFromAI = Array.isArray(aiCore?.overview?.audienceGroups)
    ? aiCore.overview.audienceGroups
    : Array.isArray(aiCore?.audienceGroups)
      ? aiCore.audienceGroups
      : [];
  const primaryAudience = audienceGroupsFromAI.find((group) => group?.isPrimary === true) || null;
  const derivedResearchActorIds =
    !hasNoResearchItems && primaryAudience
      ? [String(primaryAudience.id || primaryAudience.name || "").trim()].filter(Boolean)
      : null;

  const aiCoreForStore = aiCore && typeof aiCore === "object" ? { ...aiCore } : {};
  // Keep downstream artifacts unless explicitly regenerated in their own flows.
  // Foundation generation should not wipe already-created personas/JTBD/problem statements.
  delete aiCoreForStore.personas;
  delete aiCoreForStore.jtbds;
  delete aiCoreForStore.problemStatements;

  const generatedForStore = {
    ...aiCoreForStore,
    ...(usedHypothesisFallback ? { skipInsightGrouping: true } : {}),
    initialProblemDefinitionSeed: true,
    researchActorIds: derivedResearchActorIds,
    meta: metaPayload,
    overview: aiCore?.overview || null,
    problemDefinition: aiCore?.problemDefinition || null,
    decisions: Array.isArray(aiCore?.decisions) ? aiCore.decisions : [],
    interviewQuestions: {},
    direction: directionPayload,
  };

  // Re-assert active project before persisting generated artifacts (route transitions can race this).
  if (typeof setActiveProject === "function") {
    setActiveProject(projectId);
  }

  if (isDemoUser && typeof setMeta === "function") {
    const activeSnapshot = store?.selectActiveRefs?.()?.project || null;
    const markedSnapshot = applyDemoGenerationToProject(activeSnapshot, demoBrowserId);
    if (markedSnapshot?.meta) {
      setMeta(markedSnapshot.meta);
      demoGenerationMarked = true;
    }
  }

  if (typeof setMeta === "function") setMeta(metaPayload);
  if (typeof setFromAI === "function") setFromAI(generatedForStore);

  if (typeof setResearchItems === "function") {
    setResearchItems(researchItemsWithPersona.length ? researchItemsWithPersona : []);
  }
  if (typeof setResearchLastAIGenerationAt === "function") {
    setResearchLastAIGenerationAt(researchItemsWithPersona.length ? tNow : null);
  }

  if (typeof syncActiveProjectSnapshot === "function") {
    syncActiveProjectSnapshot();
  }

  if (projectId && storage?.saveProject) {
    const snapshot = store?.selectActiveRefs?.()?.project || null;
    if (snapshot) await storage.saveProject(projectId, snapshot);
  }

  if (demoGenerationMarked && typeof setMeta === "function") {
    setMeta({
      demoGenerationInProgress: false,
      demoGenerationStartedAt: null,
      demoGenerationHeartbeat: null,
      demoGenerationBrowserId: demoBrowserId,
    });
  }

  return { projectId };
}

export async function createProjectIfNeeded({ storage, formData, parentProjectId = null }) {
  const store = useProjectStore.getState();
  const { initializeIfEmpty, setActiveWorkspace } = store;

  if (typeof initializeIfEmpty === "function") initializeIfEmpty();

  let projectId = null;
  const projectName = formData.projectName.trim() || "Untitled project";

  // Primary path: always try the storage adapter create API first.
  // This avoids mode race conditions where storage.mode is transiently wrong.
  if (typeof storage?.createProject === "function") {
    const created = await storage.createProject(projectName);
    const createdId =
      (typeof created === "string" && created.trim()) ||
      (typeof created?.id === "string" && created.id.trim()) ||
      (typeof created?.projectId === "string" && created.projectId.trim()) ||
      (typeof created?.project?.id === "string" && created.project.id.trim()) ||
      null;
    projectId = createdId || null;
    if (!projectId) {
      const err = new Error("Project creation response did not include a project id.");
      err.code = "project_create_missing_id";
      throw err;
    }
    if (store.loadSnapshot) {
      const wsId = store.activeWorkspaceId || store.workspaces?.allIds?.[0] || null;
      const emptyProject = makeEmptyProject({
        id: projectId,
        workspaceId: wsId,
        projectName,
        projectType: formData.projectType || "new",
        productDescription: formData.productDescription || "",
        parentProjectId,
      });
      store.loadSnapshot(emptyProject);
    }
  }

  if (!projectId && store.workspaces?.allIds?.length && typeof setActiveWorkspace === "function") {
    setActiveWorkspace(store.workspaces.allIds[0]);
  }

  return projectId;
}
